import { useMemo } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { parseEntityRef } from '@backstage/catalog-model';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../core/i18n';
import type { Node, Edge } from '@xyflow/react';

export interface EntityNodeData {
  label: string;
  kind: string;
  namespace: string;
  isCurrent: boolean;
  [key: string]: unknown;
}

export interface UseEntityGraphOptions {
  direction?: 'TB' | 'LR';
  excludeRelations?: string[];
  nodeWidth?: number;
  nodeHeight?: number;
  rankSep?: number;
  nodeSep?: number;
}

type NodeCategory = 'parent' | 'child' | 'peer';

const relationLabelKeys: Record<string, string> = {
  ownedBy: 'relations.labels.ownedBy',
  partOf: 'relations.labels.partOf',
  hasPart: 'relations.labels.hasPart',
  providesApi: 'relations.labels.providesApi',
  consumesApi: 'relations.labels.consumesApi',
  apiProvidedBy: 'relations.labels.apiProvidedBy',
  apiConsumedBy: 'relations.labels.apiConsumedBy',
  dependsOn: 'relations.labels.dependsOn',
  dependencyOf: 'relations.labels.dependencyOf',
  parentOf: 'relations.labels.parentOf',
  childOf: 'relations.labels.childOf',
  memberOf: 'relations.labels.memberOf',
  hasMember: 'relations.labels.hasMember',
  ownerOf: 'relations.labels.ownerOf',
};

const inverseOf: Record<string, string> = {
  dependencyOf: 'dependsOn',
  partOf: 'hasPart',
  apiProvidedBy: 'providesApi',
  apiConsumedBy: 'consumesApi',
  memberOf: 'hasMember',
  childOf: 'parentOf',
  ownerOf: 'ownedBy',
};

// Relations where the target is semantically "above" the center entity
const parentRelTypes = new Set(['partOf', 'memberOf', 'childOf', 'ownedBy']);
// Relations where the target is semantically "below" the center entity
const childRelTypes = new Set(['hasPart', 'hasMember', 'parentOf', 'ownerOf']);

function edgeCategory(isParent: boolean, isChild: boolean): NodeCategory {
  if (isParent) return 'parent';
  if (isChild) return 'child';
  return 'peer';
}

function canonicalKey(a: string, b: string, relType: string): string {
  const canonical = inverseOf[relType] || relType;
  return a < b ? `${a}|${b}|${canonical}` : `${b}|${a}|${canonical}`;
}

// ── Layout helpers (all work in center-based coordinates) ──

/** Spread nodes in a horizontal row centered at X=0. */
function spreadRow(
  group: Node[],
  fixedY: number,
  w: number,
  gap: number,
  set: (n: Node, cx: number, cy: number) => void,
) {
  if (!group.length) return;
  const total = group.length * w + (group.length - 1) * gap;
  let cx = -total / 2 + w / 2;
  for (const node of group) {
    set(node, cx, fixedY);
    cx += w + gap;
  }
}

/** Spread nodes in a vertical column centered at Y=0. */
function spreadCol(
  group: Node[],
  fixedX: number,
  h: number,
  gap: number,
  set: (n: Node, cx: number, cy: number) => void,
) {
  if (!group.length) return;
  const total = group.length * h + (group.length - 1) * gap;
  let cy = -total / 2 + h / 2;
  for (const node of group) {
    set(node, fixedX, cy);
    cy += h + gap;
  }
}

/** Stagger nodes vertically at a fixed X, centered around Y=0. */
function staggerCol(
  group: Node[],
  fixedX: number,
  step: number,
  set: (n: Node, cx: number, cy: number) => void,
) {
  if (!group.length) return;
  const totalH = (group.length - 1) * step;
  let cy = -totalH / 2;
  for (const node of group) {
    set(node, fixedX, cy);
    cy += step;
  }
}

/** Stagger nodes horizontally at a fixed Y, centered around X=0. */
function staggerRow(
  group: Node[],
  fixedY: number,
  step: number,
  set: (n: Node, cx: number, cy: number) => void,
) {
  if (!group.length) return;
  const totalW = (group.length - 1) * step;
  let cx = -totalW / 2;
  for (const node of group) {
    set(node, cx, fixedY);
    cx += step;
  }
}

/**
 * Semantic layout: center node in the middle, parents above,
 * children below, peers on left/right sides staggered to avoid edge overlap.
 */
function applyLayout(
  nodes: Node<EntityNodeData>[],
  centerId: string,
  categories: Map<string, NodeCategory>,
  direction: 'TB' | 'LR',
  w: number,
  h: number,
  rankSep: number,
  nodeSep: number,
): void {
  const center = nodes.find(n => n.id === centerId)!;
  const parents = nodes.filter(n => categories.get(n.id) === 'parent');
  const children = nodes.filter(n => categories.get(n.id) === 'child');
  const peers = nodes.filter(n => categories.get(n.id) === 'peer');

  // Convert center-based coords → top-left position for ReactFlow
  const set = (node: Node, cx: number, cy: number) => {
    node.position = { x: cx - w / 2, y: cy - h / 2 };
  };

  set(center, 0, 0);

  if (direction === 'TB') {
    // Parents: horizontal row above
    spreadRow(parents, -(h + rankSep), w, nodeSep, set);
    // Children: horizontal row below
    spreadRow(children, h + rankSep, w, nodeSep, set);
    // Peers: alternate left/right, staggered vertically
    const peerDist = w * 1.5 + rankSep;
    const stagger = h * 1.8;
    const left = peers.filter((_, i) => i % 2 === 0);
    const right = peers.filter((_, i) => i % 2 === 1);
    staggerCol(left, -peerDist, stagger, set);
    staggerCol(right, peerDist, stagger, set);
  } else {
    // LR: parents left, children right, peers top/bottom
    spreadCol(parents, -(w + rankSep), h, nodeSep, set);
    spreadCol(children, w + rankSep, h, nodeSep, set);
    const peerDist = h * 1.5 + rankSep;
    const stagger = w * 1.8;
    const top = peers.filter((_, i) => i % 2 === 0);
    const bottom = peers.filter((_, i) => i % 2 === 1);
    staggerRow(top, -peerDist, stagger, set);
    staggerRow(bottom, peerDist, stagger, set);
  }
}

const EMPTY_EXCLUDE: string[] = [];

export function useEntityGraph(options: UseEntityGraphOptions = {}) {
  const {
    direction = 'TB',
    excludeRelations = EMPTY_EXCLUDE,
    nodeWidth = 160,
    nodeHeight = 56,
    rankSep = 120,
    nodeSep = 60,
  } = options;

  const { entity } = useEntity();
  const { t } = useTranslationRef(appTranslationRef);

  return useMemo(() => {
    const relations = entity.relations || [];
    const excludeSet = new Set(excludeRelations);
    const currentRef = `${entity.kind}:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;

    const edgeKeys = new Set<string>();
    const nodes: Node<EntityNodeData>[] = [];
    const edges: Edge[] = [];
    const nodeIds = new Set<string>();
    const nodeCategories = new Map<string, NodeCategory>();

    // Center node
    const centerId = currentRef.toLowerCase();
    nodes.push({
      id: centerId,
      type: 'entityNode',
      position: { x: 0, y: 0 },
      data: {
        label: entity.metadata.name,
        kind: entity.kind,
        namespace: entity.metadata.namespace ?? 'default',
        isCurrent: true,
      },
    });
    nodeIds.add(centerId);

    // Build nodes, classify, and create edges
    for (const rel of relations) {
      if (excludeSet.has(rel.type)) continue;

      const parsed = parseEntityRef(rel.targetRef);
      const targetId =
        `${parsed.kind}:${parsed.namespace}/${parsed.name}`.toLowerCase();

      // Classify target (priority: parent > child > peer)
      if (parentRelTypes.has(rel.type)) {
        nodeCategories.set(targetId, 'parent');
      } else if (
        childRelTypes.has(rel.type) &&
        nodeCategories.get(targetId) !== 'parent'
      ) {
        nodeCategories.set(targetId, 'child');
      } else if (!nodeCategories.has(targetId)) {
        nodeCategories.set(targetId, 'peer');
      }

      if (!nodeIds.has(targetId)) {
        nodes.push({
          id: targetId,
          type: 'entityNode',
          position: { x: 0, y: 0 },
          data: {
            label: parsed.name,
            kind: parsed.kind,
            namespace: parsed.namespace,
            isCurrent: false,
          },
        });
        nodeIds.add(targetId);
      }

      const ck = canonicalKey(centerId, targetId, rel.type);
      if (edgeKeys.has(ck)) continue;
      edgeKeys.add(ck);

      // Edge direction per-relation: parent → center, center → child/peer
      const isParent = parentRelTypes.has(rel.type);
      const isChild = childRelTypes.has(rel.type);
      const source = isParent ? targetId : centerId;
      const target = isParent ? centerId : targetId;
      const category = edgeCategory(isParent, isChild);

      edges.push({
        id: `e-${ck}`,
        source,
        target,
        type: 'relationEdge',
        data: {
          category,
          label: relationLabelKeys[rel.type]
            ? t(relationLabelKeys[rel.type] as Parameters<typeof t>[0])
            : rel.type,
        },
      });
    }

    applyLayout(
      nodes,
      centerId,
      nodeCategories,
      direction,
      nodeWidth,
      nodeHeight,
      rankSep,
      nodeSep,
    );

    return { nodes, edges };
  }, [entity, direction, excludeRelations, nodeWidth, nodeHeight, rankSep, nodeSep, t]);
}
